import { BooleanInput, coerceBooleanProperty } from '@angular/cdk/coercion';
import { GanttConfigOptions as DhtmlxGanttConfigOptions } from '../../../../assets/js/codebase/dhtmlxgantt';
import { GanttAutoSchedulingOptions, GanttBackendOptions, GanttConfigOptions, GanttEditableOptions, GanttOptions, GanttResourceOptions, PartialGanttOptions } from './gantt.types';

export function createDefaultGanttOptions(): GanttOptions {
    return {
        columns: [],
        resources: {
            enabled: false,
            columns: [{ label: 'Tittel', propertyKey: 'text' }],
        },
        autoScheduling: {
            enabled: true,
            applyConstraints: true,
            showConstraints: true,
        },
        zoom: {
            levels: [],
            activeLevel: 'week',
            trigger: 'wheel',
            useKey: 'ctrlKey',
            widthStep: 20,
            minColumnWidth: 45,
            maxColumnWidth: 180,
        },
        config: {
            criticalPath: false,
            multiSelect: false,
        },
        editing: {
            links: false,
            tasks: true,
            drag: {},
        },
    };
}

export function mergeGanttOptions(...sources: Array<PartialGanttOptions | undefined>): GanttOptions {
    const result = createDefaultGanttOptions();

    for (const source of sources) {
        if (!source) continue;

        result.columns = ((source.columns?.length ?? 0) > 0 ? source.columns : result.columns) ?? [];

        result.resources = {
            ...result.resources,
            ...source.resources,
            columns: source.resources?.columns ?? result.resources.columns,
        };

        result.autoScheduling = {
            ...result.autoScheduling,
            ...source.autoScheduling,
        };

        result.zoom = {
            ...result.zoom,
            ...source.zoom,
            levels: source.zoom?.levels ?? result.zoom.levels,
        };

        result.editing = {
            ...result.editing,
            ...source.editing,
            drag: {
                ...result.editing.drag,
                ...source.editing?.drag,
            },
        };

        result.config = {
            ...result.config,
            ...source.config,
        };

        if (source.config?.criticalPath !== undefined) {
            result.config['highlight_critical_path'] = coerceOptionalBoolean(source.config.criticalPath);
        }

        if (source.config?.multiSelect !== undefined) {
            result.config['multiselect'] = coerceOptionalBoolean(source.config.multiSelect);
        }
    }

    return result;
}

export function normalizeGanttOptions(options: PartialGanttOptions | GanttBackendOptions | undefined): PartialGanttOptions | undefined {
    if (!options) return undefined;

    const { columns, config, editing, resources, autoScheduling, zoom, ...flatOptions } = options;
    const normalized: PartialGanttOptions = {};
    const normalizedEditing: Partial<GanttEditableOptions> = { ...editing };
    const normalizedConfig: GanttConfigOptions = normalizeConfigAliases({ ...config });
    const normalizedResources: Partial<GanttResourceOptions> = { ...resources };
    const normalizedAutoScheduling: Partial<GanttAutoSchedulingOptions> = { ...autoScheduling };
    const normalizedZoom = { ...zoom };
    const flatConfig: Record<string, any> = { ...flatOptions };

    if (columns !== undefined) {
        normalized.columns = columns;
    }

    if (config?.['resources'] !== undefined) {
        applyLegacyResourceConfig(normalizedResources, config['resources']);
        delete normalizedConfig['resources'];
    }

    mapBackendEditingOptions(flatConfig, normalizedEditing);
    mapBackendResourceOptions(flatConfig, normalizedResources);
    normalizeAutoSchedulingAliases(normalizedAutoScheduling);
    mapLegacyAutoSchedulingConfig(normalizedAutoScheduling, normalizedConfig);
    mapBackendAutoSchedulingOptions(flatConfig, normalizedAutoScheduling);
    mapBackendConfigAliases(flatConfig, normalizedConfig);
    removeBackendOnlyOptions(flatConfig);
    mapBackendNativeConfigOptions(flatConfig, normalizedConfig);

    if (Object.keys(normalizedEditing).length > 0) {
        normalized.editing = normalizedEditing;
    }

    if (Object.keys(normalizedResources).length > 0) {
        normalized.resources = normalizedResources;
    }

    if (Object.keys(normalizedAutoScheduling).length > 0) {
        normalized.autoScheduling = normalizedAutoScheduling;
    }

    if (Object.keys(normalizedZoom).length > 0) {
        normalized.zoom = normalizedZoom;
    }

    if (Object.keys(normalizedConfig).length > 0) {
        normalized.config = normalizedConfig;
    }

    return normalized;
}

export function toNativeGanttConfig(options: GanttOptions): Partial<DhtmlxGanttConfigOptions> {
    const { criticalPath, multiSelect, ...rawNativeConfig } = options.config;
    const nativeConfig = removeNullishProperties(rawNativeConfig);
    const autoSchedulingConfig = typeof options.autoScheduling.config === 'object' && options.autoScheduling.config !== null ? options.autoScheduling.config : undefined;

    return removeNullishProperties({
        ...nativeConfig,
        auto_scheduling: toNativeAutoSchedulingConfig(options.autoScheduling),
        auto_scheduling_compatibility: options.autoScheduling.compatibility ?? nativeConfig['auto_scheduling_compatibility'],
        auto_scheduling_descendant_links: options.autoScheduling.descendantLinks ?? nativeConfig['auto_scheduling_descendant_links'],
        auto_scheduling_initial: options.autoScheduling.scheduleOnParse ?? nativeConfig['auto_scheduling_initial'],
        auto_scheduling_move_projects: options.autoScheduling.moveProjects ?? nativeConfig['auto_scheduling_move_projects'],
        auto_scheduling_project_constraint: options.autoScheduling.projectConstraint ?? nativeConfig['auto_scheduling_project_constraint'],
        auto_scheduling_strict: options.autoScheduling.strict ?? nativeConfig['auto_scheduling_strict'],
        auto_scheduling_use_progress: options.autoScheduling.useProgress ?? nativeConfig['auto_scheduling_use_progress'],
        schedule_from_end: options.autoScheduling.scheduleFromEnd ?? autoSchedulingConfig?.schedule_from_end ?? nativeConfig['schedule_from_end'],
        highlight_critical_path: options.config['highlight_critical_path'] ?? criticalPath,
        multiselect: options.config['multiselect'] ?? multiSelect,
    }) as Partial<DhtmlxGanttConfigOptions>;
}

export function toNativeAutoSchedulingConfig(autoScheduling: GanttAutoSchedulingOptions): DhtmlxGanttConfigOptions['auto_scheduling'] {
    if (autoScheduling.config !== undefined) {
        return autoScheduling.config;
    }

    return removeNullishProperties({
        enabled: autoScheduling.enabled,
        show_constraints: autoScheduling.showConstraints,
        apply_constraints: autoScheduling.applyConstraints,
        gap_behavior: autoScheduling.gapBehavior,
        descendant_links: autoScheduling.descendantLinks,
        schedule_on_parse: autoScheduling.scheduleOnParse,
        move_projects: autoScheduling.moveProjects,
        use_progress: autoScheduling.useProgress,
        schedule_from_end: autoScheduling.scheduleFromEnd,
        project_constraint: autoScheduling.projectConstraint,
    }) as DhtmlxGanttConfigOptions['auto_scheduling'];
}

export function customCoerceBooleanProperty(value: BooleanInput): boolean {
    // @ts-ignore
    if (value === false || value === 'false' || value === 0 || value === '0') {
        return false;
    }

    return coerceBooleanProperty(value);
}

export function removeNullishProperties<TType extends Record<string, any>>(value: TType): Partial<TType> {
    return Object.entries(value).reduce<Partial<TType>>((result, [key, item]) => {
        if (item !== null && item !== undefined) {
            result[key as keyof TType] = item;
        }

        return result;
    }, {});
}

function mapBackendEditingOptions(flatOptions: Record<string, any>, editing: Partial<GanttEditableOptions>): void {
    if (flatOptions['edit_links'] !== undefined) {
        editing.links = coerceOptionalBoolean(flatOptions['edit_links']);
        delete flatOptions['edit_links'];
    }

    if (flatOptions['edit_tasks'] !== undefined) {
        editing.tasks = coerceOptionalBoolean(flatOptions['edit_tasks']);
        delete flatOptions['edit_tasks'];
    }
}

function mapBackendResourceOptions(flatOptions: Record<string, any>, resources: Partial<GanttResourceOptions>): void {
    if (flatOptions['resources_enabled'] !== undefined) {
        resources.enabled = coerceOptionalBoolean(flatOptions['resources_enabled']);
        delete flatOptions['resources_enabled'];
    }

    if (flatOptions['resource_columns'] !== undefined) {
        resources.columns = flatOptions['resource_columns'];
        delete flatOptions['resource_columns'];
    }

    if (flatOptions['resources_config'] !== undefined) {
        resources.config = flatOptions['resources_config'];
        delete flatOptions['resources_config'];
    }
}

function mapBackendConfigAliases(flatOptions: Record<string, any>, config: GanttConfigOptions): void {
    mapBackendConfigAlias(flatOptions, config, 'critical_path', 'highlight_critical_path', true);
    mapBackendConfigAlias(flatOptions, config, 'multi_select', 'multiselect', true);
}

function mapBackendAutoSchedulingOptions(flatOptions: Record<string, any>, autoScheduling: Partial<GanttAutoSchedulingOptions>): void {
    mapBackendAutoSchedulingAlias(flatOptions, autoScheduling, 'auto_scheduling_enabled', 'enabled', true);
    mapBackendAutoSchedulingAlias(flatOptions, autoScheduling, 'auto_scheduling_show_constraints', 'showConstraints', true);
    mapBackendAutoSchedulingAlias(flatOptions, autoScheduling, 'auto_scheduling_apply_constraints', 'applyConstraints', true);
    mapBackendAutoSchedulingAlias(flatOptions, autoScheduling, 'auto_scheduling_gap_behavior', 'gapBehavior');
    mapBackendAutoSchedulingAlias(flatOptions, autoScheduling, 'auto_scheduling_descendant_links', 'descendantLinks', true);
    mapBackendAutoSchedulingAlias(flatOptions, autoScheduling, 'auto_scheduling_schedule_on_parse', 'scheduleOnParse', true);
    mapBackendAutoSchedulingAlias(flatOptions, autoScheduling, 'auto_scheduling_initial', 'scheduleOnParse', true);
    mapBackendAutoSchedulingAlias(flatOptions, autoScheduling, 'auto_scheduling_move_projects', 'moveProjects', true);
    mapBackendAutoSchedulingAlias(flatOptions, autoScheduling, 'auto_scheduling_use_progress', 'useProgress', true);
    mapBackendAutoSchedulingAlias(flatOptions, autoScheduling, 'auto_scheduling_schedule_from_end', 'scheduleFromEnd', true);
    mapBackendAutoSchedulingAlias(flatOptions, autoScheduling, 'auto_scheduling_project_constraint', 'projectConstraint', true);
    mapBackendAutoSchedulingAlias(flatOptions, autoScheduling, 'auto_scheduling_compatibility', 'compatibility', true);
    mapBackendAutoSchedulingAlias(flatOptions, autoScheduling, 'auto_scheduling_strict', 'strict', true);
}

function mapBackendAutoSchedulingAlias(flatOptions: Record<string, any>, autoScheduling: Partial<GanttAutoSchedulingOptions>, backendKey: string, configKey: keyof GanttAutoSchedulingOptions, coerceBoolean = false): void {
    if (flatOptions[backendKey] === undefined) return;

    autoScheduling[configKey] = coerceBoolean ? coerceOptionalBoolean(flatOptions[backendKey]) : flatOptions[backendKey];
    delete flatOptions[backendKey];
}

function mapBackendConfigAlias(flatOptions: Record<string, any>, config: GanttConfigOptions, backendKey: string, configKey: keyof GanttConfigOptions, coerceBoolean = false): void {
    if (flatOptions[backendKey] === undefined) return;

    config[configKey] = coerceBoolean ? coerceOptionalBoolean(flatOptions[backendKey]) : flatOptions[backendKey];
    delete flatOptions[backendKey];
}

function removeBackendOnlyOptions(flatOptions: Record<string, any>): void {
    delete flatOptions['keyno'];
}

function mapBackendNativeConfigOptions(flatOptions: Record<string, any>, config: GanttConfigOptions): void {
    Object.assign(config, removeNullishProperties(flatOptions));
}

function normalizeConfigAliases(config: GanttConfigOptions): GanttConfigOptions {
    if (config['critical_path'] !== undefined) {
        config['highlight_critical_path'] = coerceOptionalBoolean(config['critical_path']);
        delete config['critical_path'];
    }

    if (config['multi_select'] !== undefined) {
        config['multiselect'] = coerceOptionalBoolean(config['multi_select']);
        delete config['multi_select'];
    }

    return config;
}

function normalizeAutoSchedulingAliases(autoScheduling: Partial<GanttAutoSchedulingOptions>): void {
    const rawAutoScheduling = autoScheduling as Record<string, any>;

    mapAutoSchedulingAlias(rawAutoScheduling, autoScheduling, 'show_constraints', 'showConstraints', true);
    mapAutoSchedulingAlias(rawAutoScheduling, autoScheduling, 'apply_constraints', 'applyConstraints', true);
    mapAutoSchedulingAlias(rawAutoScheduling, autoScheduling, 'gap_behavior', 'gapBehavior');
    mapAutoSchedulingAlias(rawAutoScheduling, autoScheduling, 'descendant_links', 'descendantLinks', true);
    mapAutoSchedulingAlias(rawAutoScheduling, autoScheduling, 'schedule_on_parse', 'scheduleOnParse', true);
    mapAutoSchedulingAlias(rawAutoScheduling, autoScheduling, 'initial', 'scheduleOnParse', true);
    mapAutoSchedulingAlias(rawAutoScheduling, autoScheduling, 'move_projects', 'moveProjects', true);
    mapAutoSchedulingAlias(rawAutoScheduling, autoScheduling, 'use_progress', 'useProgress', true);
    mapAutoSchedulingAlias(rawAutoScheduling, autoScheduling, 'schedule_from_end', 'scheduleFromEnd', true);
    mapAutoSchedulingAlias(rawAutoScheduling, autoScheduling, 'project_constraint', 'projectConstraint', true);
    mapAutoSchedulingAlias(rawAutoScheduling, autoScheduling, 'compatibility', 'compatibility', true);
    mapAutoSchedulingAlias(rawAutoScheduling, autoScheduling, 'strict', 'strict', true);
}

function mapAutoSchedulingAlias(rawAutoScheduling: Record<string, any>, autoScheduling: Partial<GanttAutoSchedulingOptions>, sourceKey: string, targetKey: keyof GanttAutoSchedulingOptions, coerceBoolean = false): void {
    if (rawAutoScheduling[sourceKey] === undefined) return;

    autoScheduling[targetKey] = coerceBoolean ? coerceOptionalBoolean(rawAutoScheduling[sourceKey]) : rawAutoScheduling[sourceKey];
    delete rawAutoScheduling[sourceKey];
}

function mapLegacyAutoSchedulingConfig(autoScheduling: Partial<GanttAutoSchedulingOptions>, config: GanttConfigOptions): void {
    if (config['auto_scheduling'] === undefined) return;

    const legacyAutoScheduling = config['auto_scheduling'];
    delete config['auto_scheduling'];

    if (typeof legacyAutoScheduling !== 'object' || legacyAutoScheduling === null) {
        autoScheduling.config = legacyAutoScheduling;
        autoScheduling.enabled = !!legacyAutoScheduling;
        return;
    }

    autoScheduling.enabled = legacyAutoScheduling.enabled ?? autoScheduling.enabled;
    autoScheduling.showConstraints = legacyAutoScheduling.show_constraints ?? autoScheduling.showConstraints;
    autoScheduling.applyConstraints = legacyAutoScheduling.apply_constraints ?? autoScheduling.applyConstraints;
    autoScheduling.gapBehavior = legacyAutoScheduling.gap_behavior ?? autoScheduling.gapBehavior;
    autoScheduling.descendantLinks = legacyAutoScheduling.descendant_links ?? autoScheduling.descendantLinks;
    autoScheduling.scheduleOnParse = legacyAutoScheduling.schedule_on_parse ?? autoScheduling.scheduleOnParse;
    autoScheduling.moveProjects = legacyAutoScheduling.move_projects ?? autoScheduling.moveProjects;
    autoScheduling.useProgress = legacyAutoScheduling.use_progress ?? autoScheduling.useProgress;
    autoScheduling.scheduleFromEnd = legacyAutoScheduling.schedule_from_end ?? autoScheduling.scheduleFromEnd;
    autoScheduling.projectConstraint = legacyAutoScheduling.project_constraint ?? autoScheduling.projectConstraint;
}

function applyLegacyResourceConfig(resources: Partial<GanttResourceOptions>, legacyResources: GanttResourceOptions | DhtmlxGanttConfigOptions['resources']): void {
    if (isWrapperResourceOptions(legacyResources)) {
        Object.assign(resources, legacyResources);
        return;
    }

    resources.enabled = !!legacyResources;
    resources.config = legacyResources;
}

function isWrapperResourceOptions(resources: unknown): resources is GanttResourceOptions {
    return typeof resources === 'object' && resources !== null && ('enabled' in resources || 'columns' in resources || 'config' in resources);
}

function coerceOptionalBoolean(value: BooleanInput | null | undefined): boolean | undefined {
    if (value === null || value === undefined) return undefined;
    return customCoerceBooleanProperty(value);
}
